import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { useNavigate } from 'react-router-dom'
import { roundOffAndFormatNumberWithCurrency, isWithinDays } from '../../../../utils/utils'
import { openEliteLandingPage, trackEvent } from '../../../../utils/appBridge'
import { EventNames, type ElitePlanOfferType } from '../../../../types/misc.types'
import PrimaryButton from '../../../ui-components/PrimaryButton'
import PlanBanner from './PlanBanner'
import { FamilyRiskProfilePromptSheet, RiskProfilePromptSheet } from '../RiskProfilePromptSheets'
import { EliteOfferBanner } from '../../../elite/landing-page/versions/landing-page-head/EliteOfferBanner'
import { useAppMetaContent } from '../../../../state-management/providers/AppMetaContentProvider'

const RebalancePlan = ({
  isUserElite,
  numberOfFundsToRebalance,
  missedGains,
  riskProfile,
  rebalanceAvailable,
  numberOfFundsExcludedFromRebalance,
  rebalanceSnapshotDate,
  planDurationInMonths,
  rebalancePlanCount,
  throughFamilyAccess,
  primaryUserName,
  secondaryUserName,
  consentText,
  secondaryUserPhoneNumber,
  specialOffering,
  signupOffer,
  offerStartTime,
  offerExpiryTime,
  offerType,
  membershipFees
}: {
  isUserElite: boolean
  numberOfFundsToRebalance: number
  missedGains: number
  riskProfile: string | null
  rebalanceAvailable: boolean
  numberOfFundsExcludedFromRebalance: number
  rebalanceSnapshotDate?: number
  planDurationInMonths: number
  rebalancePlanCount: number
  throughFamilyAccess?: boolean
  primaryUserName?: string
  secondaryUserName?: string
  consentText?: string
  secondaryUserPhoneNumber?: string
  specialOffering?: boolean
  signupOffer?: boolean
  offerStartTime?: number | null
  offerExpiryTime?: number | null
  offerType?: ElitePlanOfferType | null
  membershipFees?: number
}) => {
  const { openBottomSheet, closeBottomSheet, destroyBottomSheet } = useBottomSheet()
  const navigate = useNavigate()
  const shouldShowBanner = isWithinDays(rebalanceSnapshotDate, 30)
  const { getEliteMembersDisplay } = useAppMetaContent()
  const eliteMembersDisplay = getEliteMembersDisplay('short')
  const handleClick = () => {
    if (isUserElite && (!riskProfile || riskProfile === 'null')) {
      if (throughFamilyAccess && consentText) {
        openBottomSheet(
          <FamilyRiskProfilePromptSheet
            onClose={closeBottomSheet}
            onDestroy={destroyBottomSheet}
            onNavigate={navigate}
            familyMemberName={secondaryUserName ?? ''}
            consentText={consentText ?? ''}
            familyMemberPhoneNumber={secondaryUserPhoneNumber ?? ''}
          />
        )
      } else {
        openBottomSheet(<RiskProfilePromptSheet onClose={closeBottomSheet} onNavigate={navigate} />)
      }
    } else {
      isUserElite ? navigate('/rebalance-plan') : openEliteLandingPage()
    }
  }
  return (
    <div className='text-brown-dark bg-[#FFF4EB]'>
      {rebalanceAvailable && numberOfFundsToRebalance === 0 && numberOfFundsExcludedFromRebalance === 0 ? (
        <h2
          className='text-[22px] text-orange10 text-left'
          aria-label="We analysed your portfolio and it's already in top shape!"
        >
          {/* aria-hidden: <strong> was a second TalkBack stop + re-announced "top shape" (Reg_Power_Rebalancing_B03) */}
          <span aria-hidden='true'>
            We analysed your portfolio and it's already in <span className='font-[700]'>top shape!</span>
          </span>
        </h2>
      ) : isUserElite ? (
        <h2 className='text-[22px] text-orange10 text-left'>Your Rebalancing Plan</h2>
      ) : (
        <h2
          className='text-[22px] text-orange10 text-left'
          aria-label='We analysed your portfolio and found it needs rebalancing'
        >
          {/* aria-hidden: emphasis span was a second TalkBack stop (same pattern as Reg_Power_Rebalancing_B03) */}
          <span aria-hidden='true'>
            We analysed your portfolio and found it <span className='font-medium'>needs rebalancing</span>
          </span>
        </h2>
      )}
      {numberOfFundsToRebalance === 0 && numberOfFundsExcludedFromRebalance > 0 ? (
        <div className={`relative ${shouldShowBanner ? 'mt-[58px]' : ''}`}>
          <PlanBanner
            rebalanceSnapshotDate={rebalanceSnapshotDate}
            planDurationInMonths={planDurationInMonths}
            rebalancePlanCount={rebalancePlanCount}
          />
          <div className='flex items-center bg-white rounded-2xl px-4 shadow-[2px_2px_0_0#CC5C2D] my-6 z-10 relative'>
            <div
              className='flex flex-col py-4 relative text-left'
              aria-label={getUnderReviewMetricsLabel(numberOfFundsExcludedFromRebalance, missedGains)}
            >
              <span aria-hidden='true' className='text-[32px] font-medium text-orange10 leading-none'>
                {numberOfFundsExcludedFromRebalance}
              </span>
              <span aria-hidden='true' className='text-base text-orange10'>
                {numberOfFundsExcludedFromRebalance > 1 ? 'Funds' : 'Fund'} under review
              </span>
              {missedGains > 0 && (
                <span aria-hidden='true' className='text-base text-orange30 mt-1'>
                  {roundOffAndFormatNumberWithCurrency(missedGains)} missed gains
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className={`relative ${shouldShowBanner ? 'mt-[58px]' : ''}`}>
          <PlanBanner
            rebalanceSnapshotDate={rebalanceSnapshotDate}
            planDurationInMonths={planDurationInMonths}
            rebalancePlanCount={rebalancePlanCount}
          />
          <div className='flex z-10 relative items-center bg-white rounded-2xl px-4 shadow-[2px_2px_0_0#CC5C2D] my-6'>
            <div
              className='flex flex-col py-4 relative text-left'
              aria-label={getFundsToRebalanceMetricsLabel(
                numberOfFundsToRebalance,
                numberOfFundsExcludedFromRebalance,
                missedGains
              )}
            >
              <span aria-hidden='true' className='text-[32px] font-medium text-orange10 leading-none'>
                {numberOfFundsToRebalance}
              </span>
              <span aria-hidden='true' className='text-base text-orange10'>
                {numberOfFundsToRebalance > 1 ? 'Funds' : 'Fund'} to rebalance
              </span>
              {numberOfFundsExcludedFromRebalance > 0 && (
                <span aria-hidden='true' className='text-base text-orange10'>
                  and {numberOfFundsExcludedFromRebalance}{' '}
                  {numberOfFundsExcludedFromRebalance > 1 ? 'more funds' : 'more fund'} under review
                </span>
              )}
              {missedGains > 0 && (
                <span aria-hidden='true' className='text-base text-orange30 mt-1'>
                  {roundOffAndFormatNumberWithCurrency(missedGains)} missed gains
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {!isUserElite &&
        (rebalanceAvailable && numberOfFundsToRebalance === 0 ? (
          <>
            <div className='text-base text-orange10 text-left mb-8'>
              <span className='font-medium'>Always keep your portfolio in top form with Elite.</span> Get Rebalancing Plan, Power Rank, Trends and a lot more.
            </div>
            <EliteOfferBanner
              specialOffering={specialOffering ?? false}
              signupOffer={signupOffer ?? false}
              offerStartTime={offerStartTime}
              offerExpiryTime={offerExpiryTime}
              offerType={offerType}
              membershipFees={membershipFees ?? 0}
              source='RebalancePlan'
              screenType='MF_Smart_Rebalancing'
            />
          </>
        ) : (
          <>
            <div className='text-base text-orange10 text-left mb-8'>
              Know which funds to <span className='font-medium'>exit and switch to better-performing ones</span>, with
              your Power Rebalancing plan.
            </div>
            <EliteOfferBanner
              specialOffering={specialOffering ?? false}
              signupOffer={signupOffer ?? false}
              offerStartTime={offerStartTime}
              offerExpiryTime={offerExpiryTime}
              offerType={offerType}
              membershipFees={membershipFees ?? 0}
              source='RebalancePlan'
              screenType='MF_Smart_Rebalancing'
            />
          </>
        ))}
      {isUserElite ? (
        <PrimaryButton
          isFullWidth
          label='View Plan'
          aria-label='View Plan, double tap to view plan'
          onClick={() => {
            trackEvent({
              eventName: EventNames.UserClicked,
              payload: {
                eventId: 'RebalancePlanClicked',
                buttonLabel: 'View Plan'
              }
            })
            handleClick()
          }}
          labelClassName=''
          disabled={false}
        />
      ) : (
        <div className='relative'>
          <PrimaryButton
            isFullWidth
            label='Unlock Plan'
            aria-label='Unlock Plan, double tap to unlock plan'
            onClick={() => {
              trackEvent({
                eventName: EventNames.UserClicked,
                payload: {
                  eventId: 'RebalancePlanClicked',
                  buttonLabel: 'Unlock Plan'
                }
              })
              handleClick()
            }}
            labelClassName=''
            disabled={false}
          />
        </div>
      )}
      <div
        className='text-13 text-orange10 mt-3 text-center'
        aria-label={`Trusted by ${eliteMembersDisplay} Elite members`}
      >
        <span aria-hidden='true'>
          Trusted by {eliteMembersDisplay} Elite members
        </span>
      </div>
    </div>
  )
}

export default RebalancePlan

export function getUnderReviewMetricsLabel(numberOfFundsExcludedFromRebalance: number, missedGains: number) {
  const fundWord = numberOfFundsExcludedFromRebalance > 1 ? 'Funds' : 'Fund'
  if (missedGains > 0) {
    return `${numberOfFundsExcludedFromRebalance} ${fundWord} under review, ${roundOffAndFormatNumberWithCurrency(missedGains)} missed gains`
  }
  return `${numberOfFundsExcludedFromRebalance} ${fundWord} under review`
}

export function getFundsToRebalanceMetricsLabel(
  numberOfFundsToRebalance: number,
  numberOfFundsExcludedFromRebalance: number,
  missedGains: number
) {
  const fundWord = numberOfFundsToRebalance > 1 ? 'Funds' : 'Fund'
  let label = `${numberOfFundsToRebalance} ${fundWord} to rebalance`
  if (numberOfFundsExcludedFromRebalance > 0) {
    const moreWord = numberOfFundsExcludedFromRebalance > 1 ? 'more funds' : 'more fund'
    label = `${label}, and ${numberOfFundsExcludedFromRebalance} ${moreWord} under review`
  }
  if (missedGains > 0) {
    label = `${label}, ${roundOffAndFormatNumberWithCurrency(missedGains)} missed gains`
  }
  return label
}
